import React from "react";

import Box from "veneer/Box";
import Tabs, { Tab, TabRow, TabsProvider } from "veneer/Tabs";

import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import { PRIORITY_FALLBACK } from "cortex/ui/src/fleet/constant/strategy";
import { addSpotsAction } from "cortex/ui/src/fleet/redux-store/ui-state/strategy";
import AddWebPageForm from "cortex/ui/src/fleet/view/media-library/component/AddWebPageForm";
import AddSpotMediaTable from "cortex/ui/src/fleet/view/strategy/component/AddSpotMediaTable";
import { Spot } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { Features } from "cortex/ui/src/ui/features";

type AddSpotTabKey = "applications" | "media" | "web-page";

type MediaEntryType =
  | typeof MEDIA_ENTRY_APPLICATION
  | typeof MEDIA_ENTRY_CONTENT_GROUP
  | typeof MEDIA_ENTRY_FOLDER
  | typeof MEDIA_ENTRY_USER_CONTENT
  | typeof MEDIA_ENTRY_WEBPAGE;

export interface AddSpotProps {
  defaultActiveTab?: AddSpotTabKey;
  handleSystemStrategyChange?: (rows: ReadonlyArray<Spot>) => void;
  priority: string;
  validContentTypes?: MediaEntryType[];
}

export default function AddSpotModal({
  defaultActiveTab = "media",
  handleSystemStrategyChange,
  priority,
  validContentTypes = [],
}: AddSpotProps) {
  const dispatch = useAppDispatch();
  const strategyPriority =
    priority === (PRIORITY_FALLBACK as any)?.id ? "fallback" : "main";
  const isFallback = priority === (PRIORITY_FALLBACK as any)?.id;
  if (isFallback) {
    return (
      <AddSpotMediaTable
        handleSystemStrategyChange={handleSystemStrategyChange}
        hasFilters={true}
        includeMediaTypes={[
          MEDIA_ENTRY_FOLDER,
          MEDIA_ENTRY_USER_CONTENT,
          MEDIA_ENTRY_CONTENT_GROUP,
        ]}
        initialPageSize={5}
        priority={strategyPriority}
      />
    );
  }

  return (
    <TabsProvider key="add-spot-tabs">
      <Box mb={1}>
        <TabRow />
      </Box>
      <Tabs>
        <Tab defaultSelected={defaultActiveTab === "media"} label="Media">
          <AddSpotMediaTable
            hasFilters={true}
            includeMediaTypes={[
              MEDIA_ENTRY_FOLDER,
              MEDIA_ENTRY_USER_CONTENT,
              MEDIA_ENTRY_CONTENT_GROUP,
            ]}
            priority={strategyPriority}
          />
        </Tab>

        {validContentTypes.includes(MEDIA_ENTRY_APPLICATION) && (
          <Tab
            defaultSelected={defaultActiveTab === "applications"}
            label="Applications"
          >
            <AddSpotMediaTable
              excludeColumns={["tag", "friendlyType"]}
              includeMediaTypes={[MEDIA_ENTRY_APPLICATION]}
              priority={strategyPriority}
            />
          </Tab>
        )}

        {Features.isEnabled(Features.constants.STRATEGIES_V2_WEBPAGES) &&
          validContentTypes.includes(MEDIA_ENTRY_WEBPAGE) && (
            <Tab
              defaultSelected={defaultActiveTab === "web-page"}
              label="Web Page"
            >
              <AddWebPageForm
                addSpots={(payload) => {
                  dispatch(
                    addSpotsAction({
                      priority: strategyPriority,
                      spots: payload.spots as any,
                    }),
                  );
                }}
                priority={strategyPriority}
              />
            </Tab>
          )}
      </Tabs>
    </TabsProvider>
  );
}
